import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Button, Alert } from '@trussworks/react-uswds';
import Icon from '@components/icon/Icon';
import { mdiHome } from '@mdi/js';
import LoaderButton from '@components/loader/LoaderButton';
import './addressValidator.scss';
import { useAddressValidationMutation } from '@api/services/geospatial/hooks/useAddressValidation';

const normalizeAddressPart = (value) =>
  String(value ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const isSameAddress = (candidate, enteredAddress) => {
  const candidateAddress = {
    streetAddress: candidate.attributes?.StAddr || candidate.address,
    secondaryAddress: candidate.attributes?.SubAddr || '',
    city: candidate.attributes?.City || '',
    state: candidate.attributes?.RegionAbbr || candidate.attributes?.Region || '',
    zipcode: candidate.attributes?.Postal || '',
  };

  return (
    normalizeAddressPart(candidateAddress.streetAddress) === normalizeAddressPart(enteredAddress.address1) &&
    normalizeAddressPart(candidateAddress.secondaryAddress) === normalizeAddressPart(enteredAddress.address2) &&
    normalizeAddressPart(candidateAddress.city) === normalizeAddressPart(enteredAddress.city) &&
    normalizeAddressPart(candidateAddress.state) === normalizeAddressPart(enteredAddress.state) &&
    normalizeAddressPart(candidateAddress.zipcode) === normalizeAddressPart(enteredAddress.zipcode)
  );
};

const AddressValidator = ({ address, isDisabled }) => {
  const [isValid, setIsValid] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [suggestedAddress, setSuggestedAddress] = useState(null);
  const [noCandidatesFound, setNoCandidatesFound] = useState(false);

  const hasAddressValue = [address.address1, address.city, address.zipcode].some((value) => String(value ?? '').trim());

  const { setValue } = useFormContext();
  const { mutate: checkAddress, isPending: isChecking } = useAddressValidationMutation();

  const validateAddress = useCallback(() => {
    const addressText = [address.address1, address.address2, address.city, address.state, address.zipcode]
      .filter(Boolean)
      .join(', ');

    setShowAlert(false);

    checkAddress(
      { address: addressText },
      {
        onSuccess: (data) => {
          const candidate = data.suggestions?.[0];

          if (!candidate) {
            setIsValid(false);
            setSuggestedAddress(null);
            setNoCandidatesFound(true);
            setShowAlert(true);
            return;
          }

          if (isSameAddress(candidate, address)) {
            setIsValid(true);
            setSuggestedAddress(null);
            setNoCandidatesFound(false);
            setShowAlert(true);
            return;
          }

          setIsValid(data.isValid);
          setNoCandidatesFound(false);
          setSuggestedAddress({
            streetAddress: candidate.attributes?.StAddr || candidate.address,
            secondaryAddress: candidate.attributes?.SubAddr || '',
            city: candidate.attributes?.City || '',
            state: candidate.attributes?.RegionAbbr || candidate.attributes?.Region || '',
            zipcode: candidate.attributes?.Postal || '',
          });
          setShowAlert(true);
        },
        onError: () => {
          setSuggestedAddress(null);
          setNoCandidatesFound(false);
          setIsValid(false);
          setShowAlert(true);
        },
      }
    );
  }, [address, checkAddress]);

  const populateFromSuggestion = () => {
    if (!suggestedAddress) return;

    setValue('address', suggestedAddress.streetAddress, { shouldValidate: true });
    setValue('addressTwo', suggestedAddress.secondaryAddress, { shouldValidate: true });
    setValue('city', suggestedAddress.city, { shouldValidate: true });
    setValue('state', suggestedAddress.state, { shouldValidate: true });
    setValue('zipcode', suggestedAddress.zipcode, { shouldValidate: true });

    setIsValid(true);
    setSuggestedAddress(null);
    setShowAlert(true);
  };

  return (
    <div className='address-validator'>
      <LoaderButton
        isLoading={isChecking}
        className='validate-address-btn'
        onClick={validateAddress}
        isDisabled={isDisabled || !hasAddressValue}
      >
        <Icon focusable={false} title='validate address' path={mdiHome} size={0.7} />
        VALIDATE ADDRESS
      </LoaderButton>
      {showAlert && isValid && !suggestedAddress && (
        <Alert type='success' heading='Success' headingLevel='h5'>
          Address Successfully Validated.
        </Alert>
      )}
      {showAlert && !isValid && !suggestedAddress && !noCandidatesFound && (
        <Alert type='error' heading='No Address Suggestions Available' headingLevel='h5'>
          Please ensure the address is entered correctly.
        </Alert>
      )}
      {showAlert && noCandidatesFound && (
        <Alert type='warning' heading='No Address Suggestions Available' headingLevel='h5'>
          No address suggestions could be found.
        </Alert>
      )}
      {suggestedAddress && (
        <Alert type='warning' heading='Suggested Address' headingLevel='h5'>
          <div className='suggested-address-container'>
            <div className='suggested-address'>
              <div>{suggestedAddress.streetAddress}</div>
              {suggestedAddress?.secondaryAddress && <div>{suggestedAddress?.secondaryAddress}</div>}
              <div>
                {suggestedAddress?.city}, {suggestedAddress?.state} {suggestedAddress?.zipcode}
              </div>
            </div>
            <Button className='button-small suggested-address-btn' onClick={populateFromSuggestion} accentStyle='warm'>
              USE SUGGESTED
            </Button>
          </div>
        </Alert>
      )}
    </div>
  );
};
export default AddressValidator;
